import React from 'react';
import { FileText, CheckCircle, AlertCircle, ChevronRight, Eye, FileCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type DraftStatus = 'not_started' | 'in_progress' | 'needs_review' | 'completed' | 'submitted_for_review' | 'locked';
interface ResumeDraftCardProps {
  fullName: string;
  draftStatus: DraftStatus;
  readiness: {
    percentage: number;
    completed: number;
    flagged: number;
    conflicting: number;
  };
  lastSaved: Date;
  milestones: any[];
  milestoneStatuses: Record<string, string>;
  onContinue: () => void;
  onReview: () => void;
  showSaveToast: boolean;
}
export function ResumeDraftCard({
  fullName,
  draftStatus,
  readiness,
  lastSaved,
  milestones,
  milestoneStatuses,
  onContinue,
  onReview,
  showSaveToast
}: ResumeDraftCardProps) {
  const renderStatusBadge = (status: DraftStatus) => {
    switch (status) {
      case 'not_started':
        return <span className="px-4 py-1.5 bg-disabled/20 text-secondary rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-1102">
            Not Started
          </span>;
      case 'in_progress':
        return <span className="px-4 py-1.5 bg-violetBlue/10 text-violetBlue rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-1103">
            In Progress
          </span>;
      case 'needs_review':
        return <span className="px-4 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-1104">
            Needs Review
          </span>;
      case 'completed':
        return <span className="px-4 py-1.5 bg-turquoise/10 text-turquoise rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-1105">
            Ready to Finalize
          </span>;
      case 'submitted_for_review':
        return <span className="px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-1106">
            Under Legal Review
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-full flex flex-col bg-white" data-id="element-1107">
      <AnimatePresence data-id="element-1108">
        {showSaveToast && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-primary text-white px-6 py-3.5 rounded-2xl shadow-xl flex items-center gap-3 z-50 text-base" data-id="element-1109">
            <CheckCircle className="w-5 h-5 text-turquoise" data-id="element-1110" />
            <span className="font-medium" data-id="element-1111">
              Your progress has been saved. You can resume anytime.
            </span>
          </motion.div>}
      </AnimatePresence>

      {/* Main Content Area */}
      <div className="flex-1 w-full max-w-5xl px-4 sm:px-6 lg:pl-[100px] lg:pr-8 pt-8 sm:pt-12 pb-10 sm:pb-16" data-id="element-1112">
        {/* Document Title + Status */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 sm:mb-10 pb-6 sm:pb-8 border-b border-borderColor" data-id="element-1113">
          <div className="flex items-center gap-3" data-id="element-1114">
            <div className="p-2.5 bg-violetBlue/10 rounded-xl flex-shrink-0" data-id="element-1115">
              <FileText className="w-5 h-5 text-violetBlue" data-id="element-1116" />
            </div>
            <div data-id="element-1117">
              <h1 className="text-xl sm:text-2xl font-bold text-primary tracking-tight" data-id="element-1118">
                Last Will and Testament
              </h1>
              <p className="text-sm text-secondary" data-id="element-1119">For {fullName}</p>
            </div>
          </div>
          <div className="flex-shrink-0" data-id="element-1120">{renderStatusBadge(draftStatus)}</div>
        </div>

        {/* Welcome / Alert Messages */}
        {draftStatus === 'in_progress' && <div className="mb-8 sm:mb-10" data-id="element-1121">
            <h2 className="text-xl sm:text-2xl font-semibold text-primary mb-2" data-id="element-1122">
              Welcome back, {fullName.split(' ')[0]}
            </h2>
            <p className="text-sm sm:text-base text-secondary leading-relaxed max-w-2xl" data-id="element-1123">
              Pick up where you left off — your answers are saved. Last edited{' '}
              <span className="font-medium text-primary" data-id="element-1124">
                {lastSaved.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric'
            })}
              </span>
              .
            </p>
          </div>}

        {draftStatus === 'needs_review' && <div className="mb-8 sm:mb-10 p-5 sm:p-6 rounded-2xl border border-amber-200 bg-amber-50 flex items-start gap-4" data-id="element-1125">
            <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-amber-600" data-id="element-1126" />
            <div data-id="element-1127">
              <h3 className="text-lg font-semibold text-amber-900 mb-1" data-id="element-1128">
                {readiness.flagged + readiness.conflicting} items need your
                attention
              </h3>
              <p className="text-base text-amber-800" data-id="element-1129">
                Please resolve these items before your Will can be finalized.
              </p>
            </div>
          </div>}

        {draftStatus === 'completed' && <div className="mb-8 sm:mb-10 p-5 sm:p-6 rounded-2xl border border-turquoise/30 bg-turquoise/5 flex items-start gap-4" data-id="element-1130">
            <CheckCircle className="w-6 h-6 text-turquoise flex-shrink-0 mt-0.5" data-id="element-1131" />
            <div data-id="element-1132">
              <h3 className="text-lg font-semibold text-primary mb-1" data-id="element-1133">
                Your Will draft is complete!
              </h3>
              <p className="text-base text-secondary" data-id="element-1134">
                You can now finalize your document or request a lawyer review.
              </p>
            </div>
          </div>}

        {/* Progress Card */}
        <div className="bg-aliceBlue rounded-2xl border border-borderColor p-6 sm:p-10 mb-8 sm:mb-10" data-id="element-1135">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4" data-id="element-1136">
            <div data-id="element-1137">
              <h3 className="text-lg font-semibold text-primary mb-1" data-id="element-1138">
                Overall Progress
              </h3>
              <p className="text-sm text-secondary" data-id="element-1139">
                Est. {Math.max(2, 25 - readiness.completed * 2.5)} mins left
              </p>
            </div>
            <div className="text-4xl sm:text-5xl font-bold text-violetBlue tracking-tight" data-id="element-1140">
              {readiness.percentage}%
            </div>
          </div>

          <div className="w-full bg-white rounded-full h-3.5 mb-10 overflow-hidden border border-borderColor/50" data-id="element-1141">
            <div className="bg-violetBlue h-full rounded-full transition-all duration-1000 ease-out" style={{
            width: `${readiness.percentage}%`
          }} data-id="element-1142" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8" data-id="element-1143">
            {milestones.slice(0, 9).map(m => {
            const status = milestoneStatuses[m.id];
            return <div key={m.id} className="flex items-center gap-3" data-id="element-1144">
                  {status === 'completed' ? <CheckCircle className="w-5 h-5 text-turquoise flex-shrink-0" data-id="element-1145" /> : status === 'flagged' ? <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" data-id="element-1146" /> : status === 'skipped' ? <div className="w-5 h-5 rounded-full border-2 border-disabled flex-shrink-0" data-id="element-1147" /> : status === 'in_progress' ? <div className="w-5 h-5 rounded-full border-2 border-violetBlue flex items-center justify-center flex-shrink-0" data-id="element-1148">
                      <div className="w-2 h-2 bg-violetBlue rounded-full" data-id="element-1149" />
                    </div> : <div className="w-5 h-5 rounded-full border-2 border-disabled flex-shrink-0" data-id="element-1150" />}
                  <span className={`text-sm sm:text-base truncate ${status === 'completed' ? 'text-primary' : status === 'in_progress' ? 'text-violetBlue font-semibold' : status === 'flagged' ? 'text-amber-600 font-semibold' : 'text-secondary'}`} data-id="element-1151">
                    {m.label}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-5" data-id="element-1152">
          {draftStatus === 'completed' ? <>
              <button onClick={onReview} className="flex-1 bg-aliceBlue border border-borderColor text-primary py-3.5 sm:py-4 px-6 rounded-xl font-semibold hover:border-violetBlue hover:text-violetBlue transition-all flex items-center justify-center gap-2.5 text-base" data-id="element-1153">
                <Eye className="w-5 h-5" data-id="element-1154" />
                Review Draft
              </button>
              <button onClick={onReview} className="flex-1 bg-violetBlue text-white py-3.5 sm:py-4 px-6 rounded-xl font-semibold hover:bg-[#6844E8] transition-all flex items-center justify-center gap-2.5 text-base" data-id="element-1155">
                <FileCheck className="w-5 h-5" data-id="element-1156" />
                Finalize Document
              </button>
            </> : <>
              <button onClick={onReview} className="flex-1 bg-aliceBlue border border-borderColor text-primary py-3.5 sm:py-4 px-6 rounded-xl font-semibold hover:border-violetBlue hover:text-violetBlue transition-all flex items-center justify-center gap-2.5 text-base" data-id="element-1157">
                <Eye className="w-5 h-5" data-id="element-1158" />
                Review Current Draft
              </button>
              <button onClick={onContinue} className="flex-1 bg-violetBlue text-white py-3.5 sm:py-4 px-6 rounded-xl font-semibold hover:bg-[#6844E8] transition-all flex items-center justify-center gap-2.5 text-base" data-id="element-1159">
                {draftStatus === 'needs_review' ? 'Resolve Issues' : 'Continue Your Will'}
                <ChevronRight className="w-5 h-5" data-id="element-1160" />
              </button>
            </>}
        </div>
      </div>
    </div>;
}